import type { FormEvent } from "react";
import { useCallback, useState } from "react";
import { createUseStyles } from "react-jss";
import type {
  ChecklistItem,
  ChecklistStep,
  ChecklistSubmission,
  SubmitChecklist,
} from "../../../../common/src";
import { submitChecklistSchema } from "../../../../common/src";
import { useAction } from "../../hooks/useAction";
import { useAsyncData } from "../../hooks/useAsyncData";
import type { VetBoatsTheme } from "../../theme";
import { trpc } from "../../trpc";
import { formatTimestamp } from "../../utils/format";
import { Badge } from "../shared/Badge";
import { Button } from "../shared/Button";
import { Field } from "../shared/Field";
import { Notice } from "../shared/Notice";
import { LoadError, Loading } from "../shared/StatusViews";
import { useFormStyles } from "../shared/styles";

const STEP_LABELS: Record<ChecklistStep, string> = {
  pickup: "Pickup",
  return: "Return",
};

const useStyles = createUseStyles((theme: VetBoatsTheme) => ({
  panel: {
    marginTop: 10,
    padding: 14,
    borderRadius: 6,
    background: theme.color.background,
  },
  list: {
    listStyle: "none",
    margin: "0 0 12px",
    padding: 0,
    display: "grid",
    gap: 10,
  },
  item: { display: "grid", gap: 6 },
  row: {
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  problemLink: {
    background: "none",
    border: "none",
    padding: 0,
    color: theme.color.danger,
    fontSize: 13,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  result: { display: "flex", gap: 8, alignItems: "baseline" },
  ok: { color: theme.color.success, fontWeight: 700 },
  bad: { color: theme.color.danger, fontWeight: 700 },
  meta: { color: theme.color.muted, fontSize: 13, margin: "0 0 10px" },
}));

const SubmittedChecklist = ({
  submission,
}: {
  submission: ChecklistSubmission;
}) => {
  const classes = useStyles();
  return (
    <>
      <p className={classes.meta}>
        Submitted by {submission.submittedByName} on{" "}
        {formatTimestamp(submission.submittedAt)}{" "}
        {submission.hasProblems ? (
          <Badge tone="danger">Problems reported</Badge>
        ) : (
          <Badge tone="success">All checked</Badge>
        )}
      </p>
      <ul className={classes.list}>
        {submission.items.map((item, i) => (
          <li key={i} className={classes.result}>
            {item.checked ? (
              <span className={classes.ok}>✓</span>
            ) : (
              <span className={classes.bad}>!</span>
            )}
            <span>
              {item.label}
              {item.problemNote && <strong>: {item.problemNote}</strong>}
            </span>
          </li>
        ))}
      </ul>
      {submission.notes && <p>Notes: {submission.notes}</p>}
    </>
  );
};

interface ChecklistFormProps {
  reservationId: number;
  step: ChecklistStep;
  items: ChecklistItem[];
  onSubmitted: () => void;
}

const ChecklistForm = ({
  reservationId,
  step,
  items,
  onSubmitted,
}: ChecklistFormProps) => {
  const classes = useStyles();
  const form = useFormStyles();
  const [checked, setChecked] = useState<Record<number, boolean>>({});
  const [problems, setProblems] = useState<Record<number, string | undefined>>(
    {}
  );
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);
  const submit = useAction(
    useCallback(
      (input: SubmitChecklist) =>
        trpc.reservation.submitChecklist.mutate(input),
      []
    )
  );

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const parsed = submitChecklistSchema.safeParse({
      reservationId,
      step,
      results: items.map((item) => ({
        itemId: item.id,
        checked: checked[item.id] ?? false,
        problemNote: checked[item.id] ? undefined : problems[item.id],
      })),
      notes,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message);
      return;
    }
    setError(undefined);
    if (await submit.run(parsed.data)) {
      onSubmitted();
    }
  };

  return (
    <form onSubmit={(e) => void onSubmit(e)} noValidate>
      <ul className={classes.list}>
        {items.map((item) => {
          const reporting = problems[item.id] !== undefined;
          return (
            <li key={item.id} className={classes.item}>
              <div className={classes.row}>
                <label className={form.checkbox}>
                  <input
                    type="checkbox"
                    checked={checked[item.id] ?? false}
                    onChange={(e) => {
                      setChecked((c) => ({
                        ...c,
                        [item.id]: e.target.checked,
                      }));
                      if (e.target.checked) {
                        setProblems((p) => ({ ...p, [item.id]: undefined }));
                      }
                    }}
                  />
                  <span>{item.label}</span>
                </label>
                {!checked[item.id] && !reporting && (
                  <button
                    type="button"
                    className={classes.problemLink}
                    onClick={() =>
                      setProblems((p) => ({ ...p, [item.id]: "" }))
                    }
                  >
                    Problem?
                  </button>
                )}
              </div>
              {reporting && (
                <input
                  className={form.control}
                  aria-label={`What's wrong with: ${item.label}`}
                  placeholder="What's wrong? e.g. only 2 oars aboard"
                  maxLength={500}
                  value={problems[item.id] ?? ""}
                  onChange={(e) =>
                    setProblems((p) => ({ ...p, [item.id]: e.target.value }))
                  }
                />
              )}
            </li>
          );
        })}
      </ul>
      <Field
        label="Notes (optional)"
        htmlFor={`checklist-notes-${reservationId}-${step}`}
      >
        <textarea
          id={`checklist-notes-${reservationId}-${step}`}
          className={`${form.control} ${form.textarea}`}
          maxLength={1000}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Field>
      {(error ?? submit.error) && (
        <Notice kind="error">{error ?? submit.error ?? ""}</Notice>
      )}
      <div className={form.actions}>
        <Button type="submit" disabled={submit.pending}>
          {submit.pending
            ? "Submitting…"
            : `Submit ${STEP_LABELS[step].toLowerCase()} checklist`}
        </Button>
      </div>
    </form>
  );
};

/** A trip's pickup or return checklist: fill it in, or see what was submitted. */
export const ChecklistPanel = ({
  reservationId,
  step,
}: {
  reservationId: number;
  step: ChecklistStep;
}) => {
  const classes = useStyles();
  const load = useCallback(
    (signal: AbortSignal) =>
      trpc.reservation.checklists.query({ id: reservationId }, { signal }),
    [reservationId]
  );
  const lists = useAsyncData(load);

  if (lists.error) {
    return <LoadError message={lists.error} onRetry={lists.reload} />;
  }
  if (!lists.data) {
    return <Loading label="Loading checklist…" />;
  }
  const { items, submission } = lists.data[step];
  return (
    <div className={classes.panel}>
      <h3>{STEP_LABELS[step]} checklist</h3>
      {submission ? (
        <SubmittedChecklist submission={submission} />
      ) : items.length === 0 ? (
        <p className={classes.meta}>
          The owner hasn't set up a {step} checklist for this boat.
        </p>
      ) : (
        <ChecklistForm
          reservationId={reservationId}
          step={step}
          items={items}
          onSubmitted={lists.reload}
        />
      )}
    </div>
  );
};
